import { InfoCircleOutlined } from '@ant-design/icons'
import { DatePicker } from 'antd'
import moment from 'moment'
import { useCallback } from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Checkbox } from 'sutra/components/Checkbox/Checkbox'
import { FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { SourceProps } from 'features/project-form/features/source/components/SourceProviders'

interface FBAuthResponse {
  userID: string
  accessToken: string
}

interface FBLoginResponse {
  authResponse?: FBAuthResponse
}

interface FBPageObject {
  id: string
  access_token: string
}

interface FBApiResponse<T> {
  data?: T
}

const dateFormat = 'MM/DD/YYYY'

export const Facebook = ({ formik }: SourceProps): JSX.Element => {
  const connect = useCallback(
    async (pages_messaging: boolean | undefined) => {
      const FB = window.FB as unknown as {
        login: (
          cb: (r: FBLoginResponse) => void,
          opts: Record<string, string>,
        ) => void
        api: (
          path: string,
          cb: (response: FBApiResponse<FBPageObject[]>) => void,
        ) => void
      }

      new Promise<FBAuthResponse>((resolve, reject) => {
        FB!.login(
          function (r: FBLoginResponse) {
            if (r.authResponse) {
              resolve(r.authResponse)
            } else {
              reject(false)
            }
          },
          {
            scope: pages_messaging
              ? 'pages_read_engagement,pages_show_list,pages_read_user_content,pages_messaging'
              : 'pages_read_engagement,pages_show_list,pages_read_user_content',
          },
        )
      })
        .then(
          (authResponse: FBAuthResponse) =>
            new Promise<boolean>((resolve, reject) => {
              FB!.api(
                `${authResponse.userID}/accounts`,
                function (response: FBApiResponse<FBPageObject[]>) {
                  if (response.data) {
                    const firstPage = response.data[0]
                    if (firstPage) {
                      formik.setFieldValue(
                        'access_token',
                        firstPage.access_token,
                      )
                      formik.setFieldValue('page_id', firstPage.id)
                    }
                    if (pages_messaging === undefined)
                      formik.setFieldValue('process_direct_messages', false)
                    resolve(true)
                  } else {
                    reject(false)
                  }
                },
              )
            }),
        )
        .catch((_e: unknown) => {
          alert(
            'Something went wrong. Please try again from a new tab, or try again with a hard refresh (Shift+Ctrl+R)',
          )
        })
    },
    [formik],
  )

  return (
    <>
      <FormItem
        label={
          <Tooltip content="Give your source a name to display in the charts. Make sure it's one you'll recognize.">
            <span>
              Source Title <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={formik.errors.sourceTitle ? 'error' : ''}
        help={formik.errors.sourceTitle}
        wrapperCol={{ span: 10 }}
      >
        <Input
          name="sourceTitle"
          onChange={formik.handleChange}
          value={formik.values.sourceTitle}
          $error={Boolean(formik.errors.sourceTitle)}
          placeholder="Insert title here"
        />
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Select the earliest date from which data should be pulled.">
            <span>
              Date <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        wrapperCol={{ span: 16 }}
        validateStatus={formik.errors.sourceDate ? 'error' : ''}
        help={formik.errors.sourceDate}
      >
        <DatePicker
          defaultValue={
            formik.values.sourceDate && moment(formik.values.sourceDate)
          }
          onChange={(value) => {
            if (value) {
              formik.setFieldValue('sourceDate', value.toDate())
            }
          }}
          format={dateFormat}
        />
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Check this to analyze the direct messages of the page.">
            <span>
              Direct Messages <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        wrapperCol={{ span: 16 }}
      >
        <Checkbox
          name="process_direct_messages"
          checked={formik.values.process_direct_messages}
          onChange={(checked) =>
            formik.setFieldValue('process_direct_messages', checked)
          }
        />
      </FormItem>
      <ButtonWrapper>
        <Error>{formik.submitCount > 0 && formik.errors.access_token}</Error>
        <Button
          variant="primary"
          size="md"
          onClick={() => connect(formik.values.process_direct_messages)}
        >
          Connect with Facebook
        </Button>
      </ButtonWrapper>
    </>
  )
}

const ButtonWrapper = styled.div`
  max-width: 200px;
  margin: 0 auto;
`
const Error = styled.div`
  text-align: center;
  color: #ff4d4f;
`
